import { PropsWithChildren, useState } from "react";

import {
  Box,
  Collapse,
  CollapseHeader,
  CollapsePanel,
  Heading,
  Section,
  Stack,
  Text,
} from "@vinomofo/components";

import { useMarket } from "../../hooks/use-market";

interface AccordionProps {
  label: string;
}

const Accordion = ({ label, children }: PropsWithChildren<AccordionProps>) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapse isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
      <CollapseHeader
        backgroundColor="almostWhite"
        padding="20px"
        iconSize="20px"
      >
        <Box mr="20px">
          <Heading as="h3" variant="xxs" textAlign="left">
            {label}
          </Heading>
        </Box>
      </CollapseHeader>
      <CollapsePanel backgroundColor="almostWhite" padding="0 20px 20px">
        {children}
      </CollapsePanel>
    </Collapse>
  );
};

export const FaqPanel = (): JSX.Element => {
  const {
    currentMarket: { telephone, email },
  } = useMarket();

  return (
    <Section py={60} backgroundColor="white">
      <Stack space={5}>
        <Heading as="h2" variant="xl" textAlign="center">
          Frequently asked questions
        </Heading>
        <Stack space={2}>
          <Accordion label="How do I know when my next club is processing?">
            <Text variant="sm">
              Your club case will be processed on the same day every month / two
              months / three months according to your plan frequency. There will
              also be a countdown in your club account page so you can prepare
              for your incoming vino and we will send you an email once your
              case has been dispatched.
            </Text>
          </Accordion>
          <Accordion label="How often are club cases sent out?">
            <Text variant="sm">
              Depends on your subscription. You’ll have options for delivery
              every one, two or three months.
            </Text>
          </Accordion>

          <Accordion label="When will I find out what’s in my case? Where can I find my tasting notes?">
            <Text variant="sm">
              Ah well that’s all part of the fun isn’t it?! You'll find out when
              your case arrives on your doorstep and for The Mofo Club there'll
              be a link to your tasting notes in your account page once the case
              is delivered.
            </Text>
          </Accordion>

          <Accordion label="Can I change the mix I’m currently subscribed to, to another? (e.g. Mixed to Reds)">
            <Text variant="sm">
              The Black Market Club is a set mix every month. This club isn’t
              about decisions, or options, just the best value vino you'll find
              anywhere. <br />
              <br /> The Mofo Club does offer more options but unfortunately, 
              we are unable to process a swap from mixed to red for your Mofo 
              club case. Each case has a different cost, with whites priced at 
              $159, mixed at $179, and reds at $199. These prices are specific 
              to each subscription type. If you would like to change your 
              club case, you will need to cancel your current subscription and 
              sign up for the Club Case of your choice.
            </Text>
          </Accordion>

          <Accordion label="Can I exclude a certain type of wine from my club case?">
            <Text variant="sm">
              Kind of. If you join the Mofo Club, you can choose to exclude
              certain wines from certain cases. Here are your options:
            </Text>

            <Text as="table" variant="sm">
              <thead>
                <Box as="tr">
                  <Box
                    as="th"
                    fontWeight={400}
                    textAlign="left"
                    color="text.heading"
                    pr="20px"
                  >
                    Reds
                  </Box>
                  <Box
                    as="th"
                    fontWeight={400}
                    textAlign="left"
                    color="text.heading"
                    pr="20px"
                  >
                    Whites
                  </Box>
                  <Box
                    as="th"
                    fontWeight={400}
                    textAlign="left"
                    color="text.heading"
                    pr="20px"
                  >
                    Mixed
                  </Box>
                </Box>
              </thead>
              <tbody>
                <Box as="tr">
                  <Box as="td" pr="20px">
                    Reds, but hold the pinot
                  </Box>
                  <Box as="td" pr="20px">
                    Whites, but hold the chardy
                  </Box>
                  <Box as="td" pr="20px">
                    Mixed, but hold the pinot
                  </Box>
                </Box>
                <Box as="tr">
                  <Box as="td" pr="20px">
                    Reds, but hold the grenache
                  </Box>
                  <Box as="td" pr="20px">
                    Whites, but hold the savvy b
                  </Box>
                  <Box as="td" pr="20px">
                    Mixed, but hold the savvy b
                  </Box>
                </Box>
              </tbody>
            </Text>
          </Accordion>

          <Accordion label="Can I have more than one club subscription?">
            <Text variant="sm">Of course! We encourage it.</Text>
          </Accordion>

          <Accordion label="Can I buy a club subscription for a friend?">
            <Text variant="sm">
              You’re the best friend ever! All you need to do is sign up and add
              your friend’s address details in instead of your own.
            </Text>
          </Accordion>

          <Accordion label="What happens if I don’t like a bottle? Can I return it?">
            <Text variant="sm">
              Of course, we’re more than happy to drink any wine you return!
              Just email <strong>{email}</strong> or call us on {telephone} and our customer team
              will sort you out.
            </Text>
          </Accordion>

          <Accordion label="Can I pause or skip my subscription?">
            <Text variant="sm">
              Sure. You can skip or pause your club subscription via your
              account page, email <strong>{email}</strong> or call us on {telephone}
            </Text>
          </Accordion>

          <Accordion label="How do I cancel my subscription?">
            <Text variant="sm">
              You can cancel your subscription via your account page or
              alternatively, email us at <strong>{email}</strong> or give us a call on{" "}
              {telephone}
            </Text>
          </Accordion>
        </Stack>
      </Stack>
    </Section>
  );
};
